<template>
  <div class="formSchedule" v-if="term && term.hasEndDate">
    <template v-if="term">
      <div class="formSchedule-body">
        <div class="product-price">
          <strong>{{ formatPrice(term.Package.price) }}</strong>
          / {{ term.name.toLowerCase() }}
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-sm-6 datepickers">
          <DateTimePicker
            ref="startDate"
            label="Starts"
            timeLabel="Start Time"
            v-model="schedStart"
            :disabled="!isTermValid || !isFormReady"
            :disabledDates="handleDisabledDate"
            :disabledTimes="disabledItems"
            :timeItems="startTimeItems"
            :isEdit="isEdit"
            :timeValue="schedule.startTime"
            :key="term ? term.id : null"
            @nm="computeDisabledDays"
            @pm="computeDisabledDays"
            @ny="computeDisabledDates"
            @py="computeDisabledDates"
          />
        </div>
        <div class="col-12 col-sm-6 datepickers">
          <DateTimePicker
            ref="endDate"
            label="Ends"
            timeLabel="End Time"
            v-model="schedEnd"
            dropdownPosition="right"
            :disabled="
              !isTermValid ||
                !isFormReady ||
                term.isCalculated ||
                term.calculatedBy !== 'hour'
            "
            :disabledDates="handleDisabledEndDate"
            :disabledTimes="disabledEndItems"
            :timeItems="endTimeItems"
            :isEdit="isEdit"
            :timeValue="schedule.endTime"
            :key="term ? term.id : null"
            @nm="computeDisabledDays"
          />
        </div>
      </div>
      <div>
        <div class="form-group">
          <template v-if="item.quantity === 1">
            <span>Quantity: <strong>1</strong></span>
          </template>
          <template v-else>
            <label for="order-qty">Quantity</label>
            <div v-if="item.quantity === -1" class="form-group">
              <div class="input-group">
                <input
                  class="form-control text-left qty-field"
                  type="text"
                  id="order-qty"
                  name="order-qty"
                  placeholder="Enter quantity"
                  v-model="orderPackage.quantity"
                  :style="{ color: primary }"
                  @drop.prevent
                  @input="e => (orderPackage.quantity = e.target.value)"
                  @keypress="isNumber($event)"
                  @keyup.delete="isNumber($event)"
                  @paste.prevent
                />
              </div>
            </div>
            <v-select
              v-if="item.quantity > 1"
              class="qty-select"
              name="qty"
              placeholder="Select quantity"
              :options="qtyOptions"
              v-model="orderPackage.quantity"
              :selected="orderPackage.quantity"
              :style="{ color: primary }"
              @input="term => (orderPackage.quantity = term.value)"
              ref="qtySelect"
            />
          </template>
        </div>
      </div>
    </template>
    <template v-if="!isFormReady || (!isFormReady && !isTermValid)">
      <div class="d-flex justify-content-center mt-20">
        <Spinner :theme="primary" />
      </div>
    </template>
    <template v-if="isValid">
      <div class="d-flex flex-column align-items-end justify-content-end mt-20">
        <template v-if="order">
          <ActionButton
            class="w-auto m-0 d-inline-block"
            label="Update"
            :theme="{ primary, secondary, event, danger }"
            @customEvent="handleSubmit"
          />
        </template>
        <template v-else>
          <ActionButton
            class="w-auto m-0 d-inline-block"
            label="Add to Cart"
            :theme="{ primary, secondary, event, danger }"
            @customEvent="handleSubmit"
          />
        </template>
        <span v-if="hasScheduleConflict" class="error-message"
          >This product is unavailable at that time, please select another
          schedule.</span
        >
      </div>
    </template>
  </div>
</template>

<script>
import { DateTimePicker } from "@/components";
import ActionButton from "@/components/ActionButton";
import Spinner from "@/components/Spinner";
import handleDateConvert from "@/helpers/algo/handleDateConvert";
import axios from "@/helpers/axios";
import { formatSchedule, overlap } from "@/helpers/booking";
import { dateUTC } from "@/helpers/date";
import moment from "@/helpers/moment";
import dateValidator from "@/helpers/validators/date";
import http from "axios";
import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  format,
  subDays
} from "date-fns";
import { isEmpty, cloneDeep, orderBy, sortBy, forEach } from "lodash";
import { mapGetters } from "vuex";
import { v4 as uuidv4 } from "uuid";

export default {
  components: {
    DateTimePicker,
    ActionButton,
    Spinner
  },
  props: {
    term: {
      type: Object,
      default: null
    },
    inclusions: {
      type: Array,
      default: null
    },
    orderIndex: {
      type: [Number, String],
      default: null
    },
    order: {
      type: Object,
      default: null
    },
    productId: {
      type: [Number, String],
      default: null
    },
    item: {
      type: Object,
      default: null
    },
    productLinkId: {
      type: Number,
      default: null
    },
    productLinkCollection: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    async term(val, oldVal) {
      if (val !== oldVal && this.isLoading) {
        this.handleRequest.cancel();
        this.handleRequest = null;
        this.cancelTermValidation = true;
        return;
      }
      await this.handleTermUpdate();
      this.cancelTermValidation = false;
    },
    cancelTermValidation(val) {
      if (val) return;

      this.handleTermUpdate();
    },
    isLoading(val) {
      if (val) return;

      this.cancelTermValidation = false;
    },
    cancelCalculate(val) {
      if (val) return;
      const date = moment(this.schedStart).local();
      this.setEndDate(date);
    },
    async schedStart(value) {
      if (!value) return;

      this.hasScheduleConflict = false;
      const date = new Date(value);
      const time = format(date, "h:mm A");
      const timeObj = this.startTimeItems.find(i => i.name === time);
      this.schedule.startDate = format(date, "MMMM D, YYYY");
      this.schedule.startTime = timeObj ? timeObj.value : "0";
      this.handleChangeDate();
      if (this.isCalculating) {
        this.cancelCalculate = true;
        return;
      }
      if (!this.isEdit) {
        await this.setEndDate(date);
      }

      if (this.qtyOptions.length < this.orderPackage.quantity) {
        this.orderPackage.quantity = 1;
      }
    },
    async schedEnd(value) {
      if (!value) return;

      this.hasScheduleConflict = false;
      const date = new Date(value);
      const time = format(date, "h:mm A");
      const timeObj = this.endTimeItems.find(i => i.name === time);
      this.schedule.endDate = format(date, "MMMM D, YYYY");
      this.schedule.endTime = timeObj ? timeObj.value : "0";
      this.handleChangeDate();

      this.isDeductionLoading = true;
      await this.generateItemsDeduction();

      if (this.qtyOptions.length < this.orderPackage.quantity) {
        this.orderPackage.quantity = 1;
      }
    },
    "schedule.startDate": {
      async handler(val) {
        if (Boolean(val) === false || this.isEdit) return;

        await this.handleDisabledItems();
        await this.setInitialStartTime();
      },
      deep: true
    }
  },
  async mounted() {
    await this.setInitialStartDate();
    if (this.order) {
      this.handleDateRevert(this.order.OrderPackage);
      this.handleEditCart();
      this.handleComputeDisabledDates();
    }
  },
  async created() {
    this.initialStartDate = this.$route.query.startDate
      ? format(new Date(this.$route.query.startDate), "MMMM D, YYYY")
      : format(new Date(), "MMMM D, YYYY");
    if (this.term) {
      this.isTermValid = true;
      this.isLoading = true;
    }
    if (this.order) {
      this.isEdit = true;
    }
    await this.handleComputeDisabledDates();
  },
  beforeDestroy() {
    this.cancelTermValidation = true;
  },
  data() {
    return {
      schedule: {
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null
      },
      orderPackage: {
        total: null,
        packageId: null,
        startAt: null,
        endAt: null,
        agenda: null,
        quantity: null
      },
      schedStart: null,
      schedEnd: null,
      disabledDates: {},
      disabledEndDates: {},
      disabledItems: [],
      disabledEndItems: [],
      deductEndItems: [],
      deductItems: [],
      isDeductionLoading: false,
      step: 1,
      isEdit: false,
      cancelTermValidation: false,
      handleRequest: null,
      isTermValid: false,
      isLoading: false,
      isCalculating: false,
      cancelCalculate: false,
      initialStartDate: null,
      hasScheduleConflict: false
    };
  },
  computed: {
    qtyOptions() {
      const deductions = Math.max(...this.deductItems);
      return Array.from(
        {
          length:
            deductions > 0
              ? this.item.quantity - deductions
              : this.item.quantity
        },
        (_, i) => ({
          label: i + 1,
          value: i + 1
        })
      );
    },
    today() {
      return this.formatDate(new Date(), "MMMM D, YYYY");
    },
    isValid() {
      if (this.term && this.schedule.startDate) {
        const details = { term: this.term, schedule: this.schedule };
        const { start, end } = handleDateConvert(details);
        return start && end && this.isTermValid && this.isFormReady;
      }
      return false;
    },
    isFormReady() {
      return !this.isLoading && !this.isCalculating && !this.isDeductionLoading;
    },
    isStartValid() {
      if (this.term && this.schedule.startDate) {
        const details = { term: this.term, schedule: this.schedule };
        const { start } = handleDateConvert(details);
        return start;
      }
      return false;
    },
    startTimeItems() {
      const selectedDate = Date.parse(this.schedule.startDate) || Date.now();

      return Array.apply(null, { length: 48 }).map((time, index) => {
        const value = index.toString();
        const option = new Date(
          new Date(selectedDate).setHours(0, index * 30, 0)
        );
        let isDisabled = +option < selectedDate && false;
        isDisabled = isDisabled || this.handleFlagStartTimeValue(value);

        // Disable booked option.
        if (this.disabledItems.includes(value)) {
          isDisabled = true;
        }

        return {
          value,
          name: format(option, "h:mm A"),
          disabled: isDisabled
        };
      });
    },
    endTimeItems() {
      const { startTime, startDate, endDate } = this.schedule;
      const { hasEndDate } = this.term;
      const startOfItem = startTime ? +startTime + 1 : -1;
      var flag = true;
      return Array.apply(null, { length: 48 }).map((time, index) => {
        time = +index;
        const value = time.toString();
        flag = this.handleFlagEndTimeValue(value);
        if (!flag && time === startOfItem) {
          flag = false;
        }
        if (
          this.disabledEndItems &&
          this.disabledEndItems.includes((index - 1).toString())
        ) {
          flag = true;
        } else {
          if (!flag && hasEndDate && startDate != endDate) {
            flag = false;
          }
        }
        return {
          value: value,
          name: format(new Date(0, 0, 0, 0, time * 30, 0, 0), "h:mm A"),
          disabled: flag
        };
      });
    },
    currency() {
      return "P";
    },
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    currentUserRoles() {
      return this.$store.getters.getRoles;
    },
    productSchedule() {
      return this.$store.state.schedule.productSchedule;
    }
  },
  methods: {
    ...mapGetters({
      orders: "getOrders",
      invalidOrder: "GET_INVALID_ORDER"
    }),
    async computeDisabledDates(year) {
      const id = this.productId;
      if (!this.term) return;
      const { calculatedBy, calculatedValue } = this.term;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let disabledDates = await axios
        .get(
          `/products/${id}/availability?year=${year}&timeZone=${timeZone}&calculatedBy=${calculatedBy}&calculatedValue=${calculatedValue}`
        )
        .then(response => {
          return response.data.data;
        })
        .catch(error => error);
      if (!isEmpty(disabledDates)) {
        // append blocked dates
        Object.keys(disabledDates).map(el => {
          this.disabledDates[el] = disabledDates[el];
        });
      }
    },
    formatDate(date, pattern = "MMM DD, YYYY h:mm A") {
      return format(new Date(date), pattern);
    },
    async computeDisabledDays(date) {
      let { month, year } = date;
      let start_of_month = new Date(year, month, 1).getDate();
      let end_of_month = new Date(year, month + 1, 0).getDate() + 15;
      if (this.productSchedule.length > 0) {
        for (var day = start_of_month; day <= end_of_month; day++) {
          let day_of_month = new Date(year, month, day);
          let temp = this.productSchedule.find(
            schedule =>
              schedule.shortname ===
              day_of_month.toLocaleString("en-us", { weekday: "short" })
          );
          // get available date schedule
          if (!temp) {
            this.disabledDates[
              this.formatDate(day_of_month, "MMMM D, YYYY")
            ] = true;
            this.disabledEndDates[
              this.formatDate(day_of_month, "MMMM D, YYYY")
            ] = true;
          }
        }
      }
    },
    async handleDisabledItems() {
      const { startDate, endDate } = this.schedule;
      const id = this.productId;
      let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const source = http.CancelToken.source();
      this.handleRequest = { cancel: source.cancel, msg: "Loading..." };
      if (startDate) {
        this.disabledItems = await axios
          .get(
            `/products/${id}/availability?date=${startDate}&timeZone=${timeZone}`,
            { cancelToken: source.token }
          )
          .then(response => {
            return response.data.data;
          })
          .catch(error => error);
      } else {
        this.handleOrderFromCartDates();
        return (this.disabledItems = []);
      }
      if (endDate) {
        this.disabledEndItems = await axios
          .get(
            `/products/${id}/availability?date=${endDate}&timeZone=${timeZone}`,
            { cancelToken: source.token }
          )
          .then(response => {
            return response.data.data;
          })
          .catch(error => error);
      } else {
        this.disabledEndItems = this.disabledItems;
      }
      this.handleOrderFromCartDates();
      return this.disabledItems;
    },
    handleOrderFromCartDates() {
      const cartItems = JSON.parse(JSON.stringify(this.orders())).filter(
        ci => ci.OrderPackage.identifier !== this.orderPackage.identifier
      );

      let disabledItems = cloneDeep(this.disabledItems);
      let disabledEndItems = cloneDeep(this.disabledEndItems);

      cartItems.forEach(cartItem => {
        const itemQuantity =
          cartItem.OrderPackage.itemQuantity >= 0
            ? cartItem.OrderPackage.itemQuantity
            : 1;
        const validateLimited =
          this.item.quantity >= 0
            ? this.orderPackage.quantity >= itemQuantity
            : !this.isEdit;
        const hasProduct = this.productLinkCollection
          ? this.productLinkCollection.find(
              v => v.productId === cartItem.OrderPackage.productId
            )
          : this.productId === cartItem.OrderPackage.productId;
        if (
          cartItem.OrderPackage &&
          (cartItem.OrderPackage.productId === this.orderPackage.productId ||
            hasProduct) &&
          validateLimited
        ) {
          const cartItemSched = formatSchedule(cartItem.OrderPackage);
          const { startDate, startTime, endDate, endTime } = cartItemSched;
          const dateDiff = differenceInDays(
            new Date(endDate),
            new Date(startDate)
          );

          if (startDate === this.schedule.startDate) {
            this.startTimeItems.forEach(st => {
              if (
                parseInt(st.value) >= parseInt(startTime) &&
                parseInt(st.value) < parseInt(endTime)
              ) {
                disabledItems.push(st.value);
              }
            });
          }

          if (endDate === this.schedule.endDate) {
            this.endTimeItems.forEach(et => {
              if (
                (parseInt(et.value) > parseInt(startTime) &&
                  parseInt(et.value) <= parseInt(endTime)) ||
                (parseInt(this.schedule.startTime) < parseInt(endTime) &&
                  parseInt(et.value) > parseInt(endTime))
              ) {
                disabledEndItems.push(et.value);
              }
            });
          }

          if (this.schedule.startDate === endDate) {
            this.startTimeItems.forEach(st => {
              if (
                parseInt(st.value) >= parseInt(startTime) &&
                parseInt(st.value) < parseInt(endTime)
              ) {
                disabledItems.push(st.value);
              }
            });
          }

          if (dateDiff > 0) {
            Array.apply(null, { length: dateDiff }).forEach((_, i) => {
              if (i > 0) {
                const currDate = this.formatDate(
                  addDays(startDate, i),
                  "MMMM D, YYYY"
                );
                this.disabledDates[currDate] = true;
              }
            });
          }
        }
      });

      this.disabledItems = orderBy(disabledItems, n => parseInt(n), ["asc"]);
      this.disabledEndItems = orderBy(disabledEndItems, n => parseInt(n), [
        "asc"
      ]);
    },
    handleDateRevert(orderPackage) {
      this.orderPackage = orderPackage;
      this.schedule = formatSchedule(orderPackage);

      const { startDate, startTime, endDate, endTime } = this.schedule;
      this.schedStart = this.getDateTimePickerFormat(startDate, startTime);
      this.schedEnd = this.getDateTimePickerFormat(endDate, endTime);
      const date = new Date(this.schedStart);
      this.computeDisabledDays({
        month: date.getMonth(),
        year: date.getFullYear()
      });
    },
    async handleChangeDate() {
      const details = { term: this.term, schedule: this.schedule };
      const { start, end, total } = handleDateConvert(details);

      this.orderPackage = {
        identifier:
          this.orderPackage && this.orderPackage.identifier
            ? this.orderPackage.identifier
            : uuidv4(),
        packageId: this.term.Package.id,
        productId: this.productId,
        startAt: start ? dateUTC(start.format("MMM DD, YYYY h:mm A")) : null,
        endAt: end ? dateUTC(end.format("MMM DD, YYYY h:mm A")) : null,
        quantity: this.orderPackage.quantity ? this.orderPackage.quantity : 1,
        itemQuantity: this.item.quantity,
        total
      };
      await this.handleDisabledItems();
      this.isEdit = false;
    },
    isCartOverlap() {
      const onCart = JSON.parse(JSON.stringify(this.orders())).map(
        order => order
      );

      const linkedProductsOnCart = onCart.map(orderOnCart => {
        if (!orderOnCart.Product.ProductLink) return;
        if (
          orderOnCart.Product.ProductLink.productCollectionId ===
          this.productLinkId
        ) {
          orderOnCart.Product.id = this.productId;
          return orderOnCart;
        }
      });

      const newLinkedBooking = [
        { OrderPackage: this.orderPackage, Product: { id: this.productId } }
      ];
      const overlapResult = overlap(
        linkedProductsOnCart,
        newLinkedBooking,
        this.productId,
        this.orderIndex
      );

      return overlapResult.overlap;
    },
    handleSubmit() {
      const { total, startAt, endAt, quantity } = this.orderPackage;
      if (!quantity || quantity < 1) {
        this.$toastr.warning("Please enter valid quantity", "Invalid Quantity");
        return;
      }
      if (
        !isNaN(total) &&
        total >= 0 &&
        dateValidator.isValidDate(startAt) &&
        dateValidator.isValidDate(endAt) &&
        startAt !== endAt
      ) {
        if (this.productLinkId) {
          if (this.isCartOverlap()) {
            this.hasScheduleConflict = true;
            return;
          }
        }

        let onCart = JSON.parse(JSON.stringify(this.orders())).map(
          order => order
        );
        let newBooking = [
          { OrderPackage: this.orderPackage, Product: { id: this.productId } }
        ];
        let overlapResult = overlap(
          onCart,
          newBooking,
          this.productId,
          this.orderIndex
        );
        if (overlapResult.overlap) {
          this.hasScheduleConflict = true;
        } else {
          // * AVAILABILITY VALIDATION
          let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          axios
            .get(
              `/products/${this.productId}/availability?qp[start]=${startAt}&qp[end]=${endAt}&qp[quantity]=${quantity}&timeZone=${timeZone}`
            )
            .then(async response => {
              var { available, schedule } = response.data;

              if (available) {
                this.$emit("submit", this.orderPackage);
                const routeCheck = this.$route.matched[0].name;
                let toastMessage = "Added a new booking";
                if (this.order) {
                  toastMessage = "Booking updated";
                }
                switch (routeCheck) {
                  case "user":
                    this.$toastr.success(toastMessage, "Success");
                    break;
                }
              } else {
                if (!schedule) {
                  this.$swal({
                    title: "Oops!",
                    text:
                      "The schedule for this product has been updated. Please adjust your booking accordingly.",
                    type: "warning",
                    confirmButtonText: "Update Booking"
                  }).then(() => {
                    this.$store.dispatch("SET_INVALID_ORDER", {
                      index: this.orderIndex,
                      data: this.order
                    });
                    this.handleEditCart();
                  });
                  return;
                }
                this.hasScheduleConflict = true;
              }
            });
        }
      } else if (!startAt || !endAt) {
        this.$swal("Warning", "Please fill up everything", "info");
      } else if (startAt === endAt) {
        this.$swal(
          "Warning",
          "Start and end date is the same, <br />this is an invalid input",
          "info"
        );
      } else if (total < 0) {
        this.$swal(
          "Warning",
          "End date must be greater than start date",
          "info"
        );
      } else {
        this.$swal("Warning", "Invalid inputs", "info");
      }
    },
    handleDisabledDate(date) {
      var day = this.formatDate(date, "MMMM D, YYYY");
      var cond;
      if (this.disabledDates[day] !== null) {
        cond = this.disabledDates[day];
      }
      cond =
        this.disabledDates[day] ||
        (date.getTime() < new Date().getTime() &&
          date.toLocaleDateString() !== new Date().toLocaleDateString());
      return cond;
    },
    handleDisabledEndDate(date) {
      const { calculatedBy, calculatedValue } = this.term;
      if (calculatedBy && calculatedBy === "hour") {
        const lastStartTimeItem = this.startTimeItems[47];
        if (
          !lastStartTimeItem.disabled &&
          this.schedule.startDate ===
            this.formatDate(addDays(date, -1), "MMMM D, YYYY")
        ) {
          return parseInt(this.schedule.startTime) < calculatedValue * 2;
        }
        const timeAvailability =
          parseInt(lastStartTimeItem.value) +
          1 -
          parseInt(this.schedule.startTime);
        if (
          (parseInt(this.schedule.startTime) >= 46 ||
            timeAvailability <= calculatedValue * 2) &&
          this.schedule.startDate === this.formatDate(date, "MMMM D, YYYY")
        ) {
          return true;
        }
        return (
          this.schedule.startDate !== this.formatDate(date, "MMMM D, YYYY")
        );
      }
      var day = this.formatDate(date, "MMMM D, YYYY");
      var cond;
      if (this.disabledEndDates[day] !== null) {
        cond = this.disabledEndDates[day];
      }
      cond =
        this.disabledEndDates[day] ||
        (date.getTime() < new Date().getTime() &&
          date.toLocaleDateString() !== new Date().toLocaleDateString());
      return cond;
    },
    formatPrice(price, decimal = false) {
      return `${this.currency} ${price.toLocaleString("en-US", {
        minimumFractionDigits: decimal && 2,
        maximumFractionDigits: 2
      })}`;
    },
    async setInitialStartTime() {
      await this.handleDisabledItems();
      let nextStartTime = this.startTimeItems.find(
        item => item.disabled === false
      ) || {
        disabled: true,
        name: "12:00 AM",
        value: "0"
      };

      this.schedule.startTime = nextStartTime.value;
      const formatDate = format(this.schedule.startDate, "YYYY/MM/DD");
      this.schedStart = new Date(
        formatDate + " " + nextStartTime.name
      ).toISOString();
    },
    async handleEditCart() {
      if (!this.invalidOrder()) return;

      await this.handleDisabledItems();
      const { startDate, startTime, endDate, endTime } = this.schedule;
      const { hasStartDate, hasStartTime } = this.term;
      let f_startDate = this.formatDate(startDate, "MMMM D, YYYY");
      let i_startTime = this.startTimeItems.find(
        item => item.value === startTime
      );
      let i_endTime = this.endTimeItems.find(item => item.value === endTime);
      if (differenceInDays(endDate, startDate)) {
        this.$parent.$refs.termSelect.open = true;
      } else {
        if (!this.startTimeItems.find(i => i.disabled == false)) {
          this.disabledDates[f_startDate] = true;
        }

        if (hasStartDate && this.disabledDates[f_startDate] == true) {
          this.$refs.startDate.$children[0].showCalendar = true;
          this.schedule.startTime = null;
        } else if (i_startTime.disabled || i_endTime.disabled) {
          if (hasStartTime) {
            this.$refs.startTime.showDropdown = true;
            this.schedule.startTime = null;
          } else {
            this.$refs.startDate.$children[0].showCalendar = true;
            this.schedule.startDate = null;
          }
        }
      }
      this.$store.dispatch("SET_INVALID_ORDER", null);
    },
    isNumber: function(evt) {
      const keyCode = evt.key || evt.which;

      if (keyCode === 8 || keyCode === "Backspace") {
        this.orderPackage.quantity =
          this.orderPackage.quantity <= 0 ? null : this.orderPackage.quantity;
      }

      if (evt.key) {
        const pattern = /^\d+\.?\d*$/;
        const isNumber = pattern.test(keyCode);
        if (!isNumber || (keyCode === "0" && evt.target.value.length === 0)) {
          evt.preventDefault();
        }
        return;
      }

      if (keyCode === 48 && evt.target.value.length === 0) {
        evt.preventDefault();
      }

      if (keyCode < 48 || keyCode > 57) {
        evt.preventDefault();
      }
    },
    async handleComputeDisabledDates() {
      let selectedDate = this.$route.query.startDate
        ? new Date(this.$route.query.startDate)
        : new Date();
      if (this.isEdit) {
        selectedDate = this.schedStart ? new Date(this.schedStart) : new Date();
      }
      await this.computeDisabledDates(selectedDate.getFullYear());
      await this.$store.dispatch("FETCH_PRODUCT_SCHEDULE", this.productId);
      await this.computeDisabledDays({
        month: selectedDate.getMonth(),
        year: selectedDate.getFullYear()
      });
      this.mapDisabledDates();
    },
    async resetDatePicker() {
      await this.handleComputeDisabledDates();
      const { startDate, startTime } = await this.getAvailableStartDate();
      const formatDate = format(startDate, "YYYY/MM/DD");
      this.schedule.startTime = startTime.value;
      this.schedStart = new Date(
        formatDate + " " + startTime.name
      ).toISOString();
      const date = new Date(this.schedStart);
      await this.setEndDate(date);
      this.computeDisabledDays({
        month: date.getMonth(),
        year: date.getFullYear()
      });
    },
    mapDisabledDates() {
      if (!this.term) return;
      const { calculatedBy, calculatedValue } = this.term;
      if (calculatedBy === "hour") return;
      let disabledDatesArr = [];

      forEach(cloneDeep(this.disabledDates), (value, index) => {
        disabledDatesArr.push({
          date: index,
          value
        });
      });

      disabledDatesArr = sortBy(disabledDatesArr, i => new Date(i.date));

      let dayCtr = 0;
      let ctrLimit = 1;

      disabledDatesArr.forEach((v, i) => {
        const dayToBlock = subDays(new Date(v.date), dayCtr * 2);
        const nextDay = addDays(new Date(v.date), 1);
        if (v.value) {
          if (dayCtr === 1) {
            switch (calculatedBy) {
              case "week":
                ctrLimit = calculatedValue * 7;
                break;

              case "month":
                ctrLimit = differenceInDays(
                  addMonths(new Date(v.date), calculatedValue),
                  new Date(v.date)
                );
                break;

              case "year":
                ctrLimit = differenceInDays(
                  addYears(new Date(v.date), calculatedValue),
                  new Date(v.date)
                );
                break;

              default:
                ctrLimit = calculatedValue;
                break;
            }

            this.disabledDates[
              format(subDays(new Date(v.date), 1), "MMMM D, YYYY")
            ] = true;
          }

          if (dayCtr <= ctrLimit && ctrLimit > 1) {
            this.disabledDates[format(dayToBlock, "MMMM D, YYYY")] = true;
          }
        }

        if (
          disabledDatesArr[i + 1] &&
          differenceInDays(nextDay, new Date(disabledDatesArr[i + 1].date)) *
            -1 ===
            0 &&
          disabledDatesArr[i + 1].value
        ) {
          dayCtr++;
          return;
        }
        dayCtr = 0;
        ctrLimit = 1;
      });
    },
    async getAvailableStartDate() {
      const { calculatedBy } = this.term;
      const initialDate = new Date(this.initialStartDate);
      let dateUnset = true;
      let dayIncrement = 0;
      let currCtr = 0;
      let startDate = new Date();
      let startTime = {
        disabled: true,
        name: "12:00 AM",
        value: "0"
      };
      const limitCtr =
        this.initialStartDate === format(new Date(), "MMMM D, YYYY") ? 5 : 3;
      while (dateUnset) {
        startDate = addDays(initialDate, dayIncrement);
        if (this.cancelTermValidation) {
          startTime = {
            disabled: true,
            name: "12:00 AM",
            value: "0"
          };
          this.isTermValid = false;
          break;
        }

        if (currCtr >= limitCtr) {
          this.toggleTermInvalidMessage();
          dateUnset = false;
          break;
        }

        if (!this.handleDisabledDate(startDate)) {
          this.schedule.startDate = format(startDate, "MMMM D, YYYY");
          await this.handleDisabledItems();
          const timeAvailability = this.startTimeItems.filter(
            i => i.disabled === false
          );
          if (calculatedBy === "hour" && !timeAvailability.length) {
            this.disabledDates[format(startDate, "MMMM D, YYYY")] = true;
            dayIncrement++;
            currCtr++;
            continue;
          }
          startTime = this.startTimeItems.find(i => i.disabled === false);
          if (startTime && !startTime.disabled) {
            dateUnset = false;
            this.isTermValid = true;
            break;
          }
        } else {
          dayIncrement++;
          continue;
        }
        this.disabledDates[format(startDate, "MMMM D, YYYY")] = true;
        currCtr++;
      }
      return {
        startDate: startDate,
        startTime: startTime
      };
    },
    getDateTimePickerFormat(date, timeValue) {
      const formatDate = format(new Date(date), "YYYY/MM/DD");
      const time = this.startTimeItems.find(
        item => item.value === timeValue
      ) || {
        disabled: true,
        name: "12:00 AM",
        value: "0"
      };
      return moment(formatDate + " " + time.name).toISOString();
    },
    async setInitialStartDate() {
      if (!this.term) return;
      if (this.isEdit) {
        this.isLoading = true;
        this.disabledDates = {};
        await this.handleComputeDisabledDates();
        await this.getAvailableStartDate();
        this.isLoading = false;
        return;
      }

      await this.handleDisabledItems();
      await this.handleChangeDate();
      if (await this.handleTermValidation()) {
        this.disabledDates = {};
        await this.resetDatePicker();
      }
      this.isLoading = false;
    },
    async setEndDate(startDate) {
      if (this.isEdit) return;

      this.isCalculating = true;
      let schedEndTime = this.endTimeItems[this.schedule.startTime];
      if (schedEndTime && this.isStartValid) {
        const formatDate = format(startDate, "YYYY/MM/DD");
        const schedEnd = moment(formatDate + " " + schedEndTime.name)
          .add(this.term.calculatedValue, this.term.calculatedBy)
          .toISOString();
        const endDate = new Date(schedEnd);
        const time = format(endDate, "h:mm A");
        const timeObj = this.endTimeItems.find(i => i.name === time);
        if (timeObj && timeObj.disabled) {
          this.schedule.endDate = format(endDate, "MMMM D, YYYY");
          await this.handleDisabledItems();
        }
        this.schedule.endTime = timeObj.value;
        this.schedEnd = schedEnd;
        this.isCalculating = false;
        this.cancelCalculate = false;
      }
    },
    handleFlagStartTimeValue(value) {
      const { calculatedBy, calculatedValue } = this.term;
      let flag = false;
      if (calculatedBy === "hour") {
        if (!this.disabledItems.includes(value)) {
          const tempValue = calculatedValue * 2 + (parseInt(value) - 1);
          if (tempValue >= 48 && this.disabledItems.includes("48")) {
            return true;
          }
          flag = this.disabledItems.includes(tempValue.toString());
        }
      }

      return flag;
    },
    handleFlagEndTimeValue(value) {
      let timeObj = this.startTimeItems.find(
        i => i.value === this.schedule.startTime
      );
      const date = moment(this.schedStart).local();
      const time = date.format("h:mm A");
      if (timeObj && timeObj.name !== time) {
        timeObj = this.startTimeItems.find(i => i.name === time);
        this.schedule.startTime = timeObj ? timeObj.value : "0";
      }
      const { startDate, endDate, startTime, endTime } = this.schedule;
      const { calculatedBy, calculatedValue } = this.term;
      if (calculatedBy && calculatedBy === "hour") {
        if (calculatedValue > 1) {
          if (startDate != endDate) {
            const tempTimeValues = 48 - parseInt(startTime);
            return parseInt(startTime) > parseInt(value)
              ? (tempTimeValues + parseInt(value)) % (calculatedValue * 2) > 0
              : true;
          }
          return (
            (parseInt(value) - parseInt(startTime)) % (calculatedValue * 2) >
              0 || parseInt(value) <= parseInt(startTime)
          );
        }
        if (parseInt(value) > parseInt(startTime) && startDate == endDate) {
          return (parseInt(value) - parseInt(startTime)) % 2 > 0;
        }
        if (startDate != endDate) {
          if (startTime !== "48") {
            const wholeHour = parseInt(startTime) % 2 === 0;
            if (parseInt(startTime) <= parseInt(value)) {
              return parseInt(startTime) % 2 === 0
                ? true
                : parseInt(startTime) <= parseInt(value);
            }
            return wholeHour
              ? parseInt(value) % 2 > 0
              : parseInt(value) % 2 === 0;
          }
          return (
            (parseInt(value) - parseInt(endTime)) % 2 === 0 ||
            parseInt(startTime) <= parseInt(value)
          );
        }
      }
      return calculatedBy === "hour";
    },
    toggleTermInvalidMessage() {
      this.isTermValid = false;
      this.$swal(
        "Oops!",
        "The selected term is not applicable for the product's schedule. Please select another term and try again.",
        "warning"
      );
    },
    async handleTermValidation() {
      if (this.term && this.term.calculatedBy === "hour") return true;
      if (!this.cancelTermValidation) {
        const { startAt, endAt, quantity } = this.orderPackage;
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (startAt && endAt) {
          return await axios
            .get(
              `/products/${this.productId}/availability?qp[start]=${startAt}&qp[end]=${endAt}&qp[quantity]=${quantity}&timeZone=${timeZone}`
            )
            .then(response => {
              var { schedule } = response.data;
              if (!schedule) {
                if (!this.cancelTermValidation) {
                  this.toggleTermInvalidMessage();
                }
                return false;
              }
              return true;
            });
        }
      }
      return false;
    },
    async generateItemsDeduction() {
      const { startAt, endAt, quantity } = this.orderPackage;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.deductItems = [];
      if (startAt && endAt) {
        await axios
          .get(
            `/products/${this.productId}/availability?qp[start]=${startAt}&qp[end]=${endAt}&qp[quantity]=${quantity}&timeZone=${timeZone}`
          )
          .then(response => {
            const { temp } = response.data;
            const tempData = temp;
            forEach(tempData, value => {
              this.deductItems.push(...value);
            });
          });
      }
      this.isDeductionLoading = false;
    },
    async handleTermUpdate() {
      const initialDate = this.$route.query.startDate
        ? new Date(this.$route.query.startDate)
        : new Date();
      this.disabledDates = {};
      this.isLoading = true;
      let dateUnset = true;
      let dayIncrement = 0;
      let tempDate = new Date();
      while (dateUnset) {
        const date = addDays(initialDate, dayIncrement);
        if (!this.handleDisabledDate(date) || dayIncrement >= 7) {
          tempDate = date;
          dateUnset = false;
          break;
        }
        this.disabledDates[format(date, "MMMM D, YYYY")] = true;
        dayIncrement++;
      }
      this.initialStartDate = format(tempDate, "MMMM D, YYYY");
      const tempTime = format(tempDate, "h:mm A");
      const timeObj = this.startTimeItems.find(i => i.name === tempTime);
      this.schedule = {
        startDate: format(tempDate, "MMMM D, YYYY"),
        startTime: timeObj ? timeObj.value : "0",
        endDate: null,
        endTime: null
      };
      await this.handleChangeDate();
      if (!this.cancelTermValidation) {
        this.isTermValid = await this.handleTermValidation();
        if (this.isTermValid) {
          await this.resetDatePicker();
          this.handleChangeDate();
        }
      }
      this.isLoading = false;
    }
  }
};
</script>

<style lang="scss">
.formSchedule {
  margin: 0 auto 30px;
  &-header {
    font-size: $font-size-md;
    margin-bottom: 20px;
  }
  &-body {
    font-size: $font-size-base;
    > p {
      margin-bottom: 15px;
    }
    > label {
      font-weight: normal !important;
    }
  }
  button {
    @media (max-width: 767px) {
      padding: 8px 14px;
    }
  }
  .qty-field {
    @media screen and (max-width: 639px) {
      font-size: 16px;
    }
  }
  .qty-select.open {
    .dropdown-toggle {
      border-color: $brand-primary-v2 !important;
    }
    .dropdown-menu {
      margin-top: 0.5rem;
    }
  }

  .dropdown-toggle {
    height: 40px;
    border-radius: 4px !important;
    border-color: #ccc !important;
    color: #4c4c4c !important;

    .vs__selected-options {
      padding-top: 6px !important;
      padding-bottom: 6px !important;

      input.form-control {
        @media screen and (max-width: 639px) {
          font-size: 16px;
        }
      }
    }
  }

  .row {
    padding-left: 15px;
    padding-right: 15px;
    gap: 1rem;
    flex-direction: column;

    @media (min-width: 576px) {
      flex-direction: row;
      flex-wrap: nowrap;
    }

    .datepickers {
      flex: 0 0 48%;
      padding: 0;
    }
  }

  .product-price {
    margin-bottom: 1.5rem;
    color: #4c4c4c;
  }
  .plan {
    margin-top: 30px;
    &-header {
      font-size: $font-size-md !important;
      line-height: $line-height-base;
      margin-bottom: 20px !important;
    }
    p,
    li {
      line-height: $line-height-base;
      font-size: $font-size-base;
      margin: 0;
    }
  }
  .enddate {
    &__label {
      font-size: $font-size-xs !important;
      font-weight: 500;
      margin-bottom: 0.25rem;
    }
    &__input {
      border: 1px solid #dedede;
      color: #8c8c8c;
      border-radius: 5px;
      text-align: center;
      margin: 0;
      font-size: $font-size-sm;
      padding: 8px 14px;
      margin-bottom: 0.625rem;
    }
  }

  .error-message {
    color: #fa533d;
    font-size: 0.75rem;
    margin-top: 0.5rem;
  }
}
</style>
